// frontend/src/pages/Partners/PartnerList.jsx
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { getPartners } from "../../api/partner.api.js";
import { getEntities } from "../../api/entity.api.js";
import {
  PageHeader,
  Spinner,
  EmptyState,
  formatDate,
} from "../../components/ui";
import { Plus, ChevronRight, Search, X, FileText } from "lucide-react";

// ─── MultiSelect dropdown ─────────────────────────────────────────────────────

function MultiSelect({
  label,
  options,
  selected,
  onChange,
  getLabel,
  getValue,
}) {
  const count = selected.length;
  return (
    <div className="flex flex-col gap-1 min-w-[160px]">
      <label className="text-xs text-gray-500 font-medium">
        {label}
        {count > 0 && <span className="ml-1 text-brand-600">({count})</span>}
      </label>
      <details className="group relative">
        <summary className="flex items-center justify-between w-full px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer hover:bg-gray-50 list-none text-sm text-gray-700">
          <span>{count === 0 ? `All ${label}s` : `${count} selected`}</span>
          <svg
            className="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform flex-shrink-0 ml-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </summary>
        <div className="absolute z-20 mt-1 w-full min-w-[200px] bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {options.length === 0 ? (
            <p className="px-3 py-2 text-sm text-gray-400 italic">
              No options available
            </p>
          ) : (
            options.map((opt) => {
              const val = getValue ? getValue(opt) : opt.id;
              const lbl = getLabel ? getLabel(opt) : opt.name;
              const checked = selected.includes(val);
              return (
                <label
                  key={val}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      onChange(
                        checked
                          ? selected.filter((v) => v !== val)
                          : [...selected, val],
                      )
                    }
                    className="rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                  />
                  <span className="text-sm text-gray-700">{lbl}</span>
                </label>
              );
            })
          )}
        </div>
      </details>
    </div>
  );
}

function FilterTag({ label, onRemove }) {
  return (
    <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-50 border border-brand-200 text-xs text-brand-700">
      {label}
      <button onClick={onRemove} className="hover:text-brand-900 ml-0.5">
        <X size={11} />
      </button>
    </span>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PartnerList() {
  const { isMaster } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState(searchParams.get("search") ?? "");
  const [groupIds, setGroupIds] = useState(() =>
    searchParams.getAll("groupId"),
  );
  const [entityIds, setEntityIds] = useState(() =>
    searchParams.getAll("entityId"),
  );

  // ── Sync state → URL ──────────────────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    groupIds.forEach((id) => params.append("groupId", id));
    entityIds.forEach((id) => params.append("entityId", id));
    setSearchParams(params, { replace: true });
  }, [search, groupIds, entityIds]);

  const { data: partners, isLoading } = useQuery({
    queryKey: ["partners", search, groupIds, entityIds],
    queryFn: () =>
      getPartners({
        ...(search && { search }),
        ...(groupIds.length && { groupId: groupIds }),
        ...(entityIds.length && { entityId: entityIds }),
      }),
  });

  const { data: entities } = useQuery({
    queryKey: ["entities-for-filter"],
    queryFn: () => getEntities(),
    enabled: isMaster,
  });

  const groups = entities
    ? [
        ...new Map(
          entities.filter((e) => e.group).map((e) => [e.group.id, e.group]),
        ).values(),
      ].sort((a, b) => a.name.localeCompare(b.name))
    : [];

  const activeFilterCount = groupIds.length + entityIds.length;
  const hasActiveFilters = !!(search || activeFilterCount);

  function clearFilters() {
    setSearch("");
    setGroupIds([]);
    setEntityIds([]);
  }

  return (
    <div>
      <PageHeader
        title="Partners"
        subtitle={`${partners?.length ?? 0} total`}
        action={
          isMaster && (
            <Link
              to="/partners/new"
              className="btn-primary flex items-center gap-2 text-sm"
            >
              <Plus size={16} /> New Partner
            </Link>
          )
        }
      />

      {/* ── Filter bar ───────────────────────────────────────────────────── */}
      <div className="mb-5 space-y-3">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative max-w-xs flex-1">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <input
              className="input pl-9 text-sm"
              placeholder="Search partners..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-600"
            >
              <X size={14} /> Clear all
            </button>
          )}
        </div>

        {isMaster && (
          <div className="flex flex-wrap gap-3 items-start">
            <MultiSelect
              label="Group"
              options={groups}
              selected={groupIds}
              onChange={setGroupIds}
            />
            <MultiSelect
              label="Entity"
              options={entities ?? []}
              selected={entityIds}
              onChange={setEntityIds}
            />
          </div>
        )}

        {activeFilterCount > 0 && (
          <div className="flex flex-wrap gap-2">
            {groupIds.map((gid) => (
              <FilterTag
                key={gid}
                label={`Group: ${groups.find((g) => g.id === gid)?.name ?? gid}`}
                onRemove={() => setGroupIds(groupIds.filter((v) => v !== gid))}
              />
            ))}
            {entityIds.map((eid) => (
              <FilterTag
                key={eid}
                label={`Entity: ${entities?.find((e) => e.id === eid)?.name ?? eid}`}
                onRemove={() =>
                  setEntityIds(entityIds.filter((v) => v !== eid))
                }
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Partner list ──────────────────────────────────────────────────── */}
      {isLoading ? (
        <Spinner />
      ) : !partners?.length ? (
        <EmptyState
          title="No partners found"
          description={
            hasActiveFilters ? "Try adjusting your filters." : undefined
          }
          action={
            hasActiveFilters ? (
              <button
                onClick={clearFilters}
                className="btn-secondary text-sm flex items-center gap-1.5"
              >
                <X size={13} /> Clear filters
              </button>
            ) : isMaster ? (
              <Link to="/partners/new" className="btn-primary text-sm">
                Add Partner
              </Link>
            ) : undefined
          }
        />
      ) : (
        <div className="card divide-y divide-gray-100">
          {partners.map((p) => {
            const activeCount = p._count?.agreements ?? 0;
            return (
              <Link
                key={p.id}
                to={`/partners/${p.id}`}
                className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {p.companyName}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {p.picName} · {p.email} · Since {formatDate(p.createdAt)}
                  </p>
                </div>
                <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                  <span
                    className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                      activeCount > 0
                        ? "bg-green-50 text-green-700"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    <FileText size={11} />
                    {activeCount} active
                  </span>
                  <ChevronRight size={16} className="text-gray-400" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
